import { AdminAchievements } from './pages/admin/Achievements';
import { Achievements } from './pages/Achievements';
import { AdminSupport } from './pages/admin/Support';
import { ContactPage } from './pages/Contact';
const AdminAnnouncements = lazy(() => import('./pages/admin/Announcements').then(m => ({default:m.AdminAnnouncements})));
import { reportError } from './diagnostics';
import { DirectMessagesProvider, DirectMessagesPage } from './components/DirectMessages';
import { NotificationsProvider } from './components/Notifications';
import { FriendsPanel } from './components/Friends';
import { Wishes } from './pages/Wishes';
const VerifyEmail = lazy(() =>
  import('./pages/VerifyEmail').then((m) => ({ default: m.VerifyEmail })),
);
const Invitations = lazy(() =>
  import('./pages/Invitations').then((m) => ({ default: m.Invitations })),
);
const InvitationLanding = lazy(() =>
  import('./pages/Invitations').then((m) => ({ default: m.InvitationLanding })),
);
const AdminReferrals = lazy(() =>
  import('./pages/admin/Referrals').then((m) => ({ default: m.AdminReferrals })),
);
const AdminOperations = lazy(() =>
  import('./pages/admin/Operations').then((m) => ({ default: m.AdminOperations })),
);
const AdminRankings = lazy(() =>
  import('./pages/admin/Rankings').then((m) => ({ default: m.AdminRankings })),
);
const RankingsPage = lazy(() =>
  import('./pages/Rankings').then((m) => ({ default: m.RankingsPage })),
);
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { Provider } from './store';
import { PageMetadata } from './components/PageMetadata';
import { ThemeProvider } from './theme';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { TrafficTracker } from './components/TrafficTracker';
import { PrivacyProvider } from './components/PrivacyMode';
import { WorkdayProvider } from './components/WorkdayCountdown';
const AvatarSettings = lazy(() =>
  import('./pages/AvatarSettings').then((m) => ({ default: m.AvatarSettings })),
);
const Profile = lazy(() => import('./pages/Profile').then((m) => ({ default: m.Profile })));
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const Detail = lazy(() => import('./pages/Detail').then((m) => ({ default: m.Detail })));
const Community = lazy(() => import('./pages/Community').then((m) => ({ default: m.Community })));
const Membership = lazy(() =>
  import('./pages/Membership').then((m) => ({ default: m.Membership })),
);
const Player = lazy(() => import('./pages/Player').then((m) => ({ default: m.Player })));
const AdminLayout = lazy(() =>
  import('./pages/admin/Layout').then((m) => ({ default: m.AdminLayout })),
);
const SiteOverview = lazy(() => import('./pages/admin/SiteOverview').then(m => ({default:m.SiteOverview})));
const GameAnalysis = lazy(() => import('./pages/admin/GameAnalysis').then(m => ({default:m.GameAnalysis})));
const Dashboard = lazy(() =>
  import('./pages/admin/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const AdminGames = lazy(() =>
  import('./pages/admin/Games').then((m) => ({ default: m.AdminGames })),
);
const AdminCommunity = lazy(() =>
  import('./pages/admin/Community').then((m) => ({ default: m.AdminCommunity })),
);
const AdminFeedback = lazy(() =>
  import('./pages/admin/Feedback').then((m) => ({ default: m.AdminFeedback })),
);
const AdminUsers = lazy(() =>
  import('./pages/admin/Users').then((m) => ({ default: m.AdminUsers })),
);
const AdminSettings = lazy(() =>
  import('./pages/admin/Settings').then((m) => ({ default: m.AdminSettings })),
);
const AdminDiagnostics = lazy(() => import('./pages/admin/Diagnostics').then(m => ({default:m.AdminDiagnostics})));
const AdminLogs = lazy(() => import('./pages/admin/Logs').then((m) => ({ default: m.AdminLogs })));
import './styles.css';
import './theme.css';
import './components/cloud-save-panel.css';
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(error: Error) { reportError(error, 'react-render'); }
  render() {
    return this.state.error ? (
      <div className="empty-state">
        <h2>这个页面遇到了一点问题</h2>
        <p>你的游戏与存档仍然保留。请刷新后重试。</p>
        <button className="button" onClick={() => location.reload()}>
          重新加载
        </button>
      </div>
    ) : (
      this.props.children
    );
  }
}
function App() {
  React.useEffect(() => { window.__moyuDiagnostics?.ready(); }, []);
  return (
    <ErrorBoundary>
      <Provider>
        <BrowserRouter>
          <PageMetadata />
          <DirectMessagesProvider>
            <NotificationsProvider>
              <PrivacyProvider>
                <WorkdayProvider>
                  <TrafficTracker />
                  <FriendsPanel />
                  <Suspense
                    fallback={
                      <div className="loading-panel">
                        <span className="spinner" />
                        正在打开…
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="messages/:peer" element={<DirectMessagesPage />} />
                      <Route element={<Layout />}>
                        <Route index element={<Home />} />
                        <Route path="notifications" element={<Navigate to="/" replace />} />
                        <Route path="contact" element={<ContactPage />} />
                        <Route path="rankings" element={<RankingsPage />} />
                        <Route path="achievements" element={<Achievements />} />
                        <Route path="library" element={<Home />} />
                        <Route path="favorites" element={<Home />} />
                        <Route path="recent" element={<Home />} />
                        <Route path="onboarding" element={<AvatarSettings onboarding />} />
                        <Route path="account/profile" element={<Profile />} />
                        <Route path="account/profile/edit" element={<AvatarSettings profile />} />
                        <Route path="account/avatar" element={<AvatarSettings />} />
                        <Route path="community" element={<Community />} />
                        <Route path="wishes" element={<Wishes />} />
                        <Route path="membership" element={<Membership />} />
                        <Route path="verify-email" element={<VerifyEmail />} />
                        <Route path="invite" element={<Invitations />} />
                        <Route path="i/:code" element={<InvitationLanding />} />
                        <Route path="game/:slug" element={<Detail />} />
                        <Route
                          path="*"
                          element={
                            <div className="empty-state">
                              <h2>这里还没有游戏</h2>
                              <Link to="/" className="button">
                                返回大厅
                              </Link>
                            </div>
                          }
                        />
                      </Route>
                      <Route path="login" element={<Login />} />
                      <Route path="login.html" element={<Navigate to="/login" replace />} />
                      <Route path="admin.html" element={<Navigate to="/admin" replace />} />
                      <Route path="index.html" element={<Navigate to="/" replace />} />
                      <Route path="play/:id" element={<Player />} />
                      <Route
                        path="admin/login"
                        element={<Navigate to="/login?next=%2Fadmin" replace />}
                      />
                      <Route path="admin" element={<AdminLayout />}>
                        <Route index element={<SiteOverview />} />
                        <Route path="games" element={<AdminGames />} />
                        <Route path="rankings" element={<AdminRankings />} />
                        <Route path="achievements" element={<AdminAchievements />} />
                        <Route path="analytics/games/:id" element={<GameAnalysis />} />
                        <Route path="analytics" element={<Dashboard analytics />} />
                        <Route
                          path="analytics/visitors"
                          element={<Dashboard analytics view="visitors" />}
                        />
                        <Route
                          path="analytics/traffic"
                          element={<Dashboard analytics view="traffic" />}
                        />
                        <Route
                          path="analytics/games"
                          element={<GameAnalysis />}
                        />
                        <Route
                          path="analytics/sources"
                          element={<Dashboard analytics view="sources" />}
                        />
                        <Route path="support" element={<AdminSupport />} />
                        <Route path="feedback" element={<AdminFeedback />} />
                        <Route path="community" element={<AdminCommunity />} />
                        <Route path="operations" element={<AdminOperations />} />
                        <Route path="announcements" element={<AdminAnnouncements />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="referrals" element={<AdminReferrals />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route
                          path="release-tokens"
                          element={<Navigate to="/admin/games?tokens=1" replace />}
                        />
                        <Route path="diagnostics" element={<AdminDiagnostics />} />
                        <Route path="logs" element={<AdminLogs />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </WorkdayProvider>
              </PrivacyProvider>
            </NotificationsProvider>
          </DirectMessagesProvider>
        </BrowserRouter>
      </Provider>
    </ErrorBoundary>
  );
}
ReactDOM.createRoot(document.getElementById('root')!).render(
  <ThemeProvider>
    <App />
  </ThemeProvider>,
);
